import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, Trash2, Plus, Copy } from "lucide-react";

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isComplex = (v) => isObj(v) || Array.isArray(v);
const clone = (v) => JSON.parse(JSON.stringify(v));

const singular = (key) => {
  if (!key) return "item";
  return key.endsWith("s") ? key.slice(0, -1) : key;
};

const itemTitle = (item, i) => {
  if (isObj(item)) {
    const t = item.name || item.title || item.id || item.label || item.t || item.prompt || item.email;
    if (t) return String(t).slice(0, 70);
  }
  if (typeof item === "string") return item.slice(0, 70) || `Item ${i + 1}`;
  return `Item ${i + 1}`;
};

// Function-based recursion (NOT JSX self-tags) so the JSX source-instrumentation
// build plugin doesn't follow a component cycle.
function renderLeaf(value, onChange, path) {
  if (typeof value === "boolean") {
    return (
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={`${path}-input`}
        className={`text-xs font-semibold px-3 py-1.5 rounded-md border-2 transition-colors ${value ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}
      >
        {value ? "true" : "false"}
      </button>
    );
  }
  if (typeof value === "number") {
    return (
      <Input type="number" step="any" value={value} data-testid={`${path}-input`} className="w-40 font-mono-clinical"
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
    );
  }
  const str = value ?? "";
  if (String(str).length > 48) {
    return (
      <Textarea value={str} data-testid={`${path}-input`} rows={Math.min(8, Math.ceil(String(str).length / 60) + 1)}
        className="w-full text-sm" onChange={(e) => onChange(e.target.value)} />
    );
  }
  return <Input value={str} data-testid={`${path}-input`} className="w-full text-sm" onChange={(e) => onChange(e.target.value)} />;
}

function renderObject(value, onChange, path) {
  return (
    <div className="space-y-2.5">
      {Object.entries(value).map(([k, v]) => {
        const nested = isComplex(v);
        return (
          <div key={k} className={nested ? "rounded-md border border-slate-200 bg-slate-50/50 p-3" : "flex flex-col sm:flex-row sm:items-center gap-2"}>
            <label className={`text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${nested ? "block mb-2" : "sm:w-32 shrink-0"}`}>{k}</label>
            <div className={nested ? "" : "flex-1"}>
              {renderNode(v, (nv) => onChange({ ...value, [k]: nv }), `${path}-${k}`, k)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderArray(value, onChange, path, fieldKey) {
  const setAt = (i, nv) => onChange(value.map((x, idx) => (idx === i ? nv : x)));
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const dupAt = (i) => {
    const next = [...value];
    next.splice(i + 1, 0, clone(value[i]));
    onChange(next);
  };
  const add = () => onChange([...value, value.length ? clone(value[value.length - 1]) : ""]);
  const stop = (e, fn) => { e.preventDefault(); e.stopPropagation(); fn(); };

  return (
    <div className="space-y-2" data-testid={`${path}-array`}>
      {value.map((item, i) => {
        if (!isComplex(item)) {
          return (
            <div key={i} className="flex items-center gap-2" data-testid={`${path}-row-${i}`}>
              <span className="text-[10px] text-slate-400 font-mono w-6 shrink-0">{i + 1}</span>
              <div className="flex-1">{renderNode(item, (nv) => setAt(i, nv), `${path}-${i}`, fieldKey)}</div>
              <button type="button" onClick={() => removeAt(i)} data-testid={`${path}-remove-${i}`} className="text-rose-500 p-1.5 hover:bg-rose-50 rounded">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        }
        return (
          <details key={i} className="rounded-md border border-slate-200 bg-white group" data-testid={`${path}-card-${i}`}>
            <summary className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-t-md cursor-pointer list-none" data-testid={`${path}-toggle-${i}`}>
              <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 transition-transform group-open:rotate-90" />
              <span className="text-[10px] text-slate-400 font-mono">{i + 1}</span>
              <span className="text-sm font-medium text-slate-800 truncate flex-1">{itemTitle(item, i)}</span>
              <span role="button" tabIndex={0} onClick={(e) => stop(e, () => dupAt(i))} data-testid={`${path}-duplicate-${i}`} className="text-slate-400 p-1.5 hover:bg-slate-200 rounded" title="Duplicate">
                <Copy className="w-3.5 h-3.5" />
              </span>
              <span role="button" tabIndex={0} onClick={(e) => stop(e, () => removeAt(i))} data-testid={`${path}-remove-${i}`} className="text-rose-500 p-1.5 hover:bg-rose-50 rounded" title="Remove">
                <Trash2 className="w-3.5 h-3.5" />
              </span>
            </summary>
            <div className="p-3">{renderNode(item, (nv) => setAt(i, nv), `${path}-${i}`, singular(fieldKey))}</div>
          </details>
        );
      })}
      <button type="button" onClick={add} data-testid={`${path}-add`} className="flex items-center gap-1 text-xs font-semibold text-[#0047AB] border border-[#0047AB]/30 rounded-md px-2.5 py-1.5 hover:bg-[#0047AB]/5">
        <Plus className="w-3.5 h-3.5" /> Add {singular(fieldKey)}
      </button>
    </div>
  );
}

function renderNode(value, onChange, path, fieldKey) {
  if (Array.isArray(value)) return renderArray(value, onChange, path, fieldKey);
  if (isObj(value)) return renderObject(value, onChange, path);
  return renderLeaf(value, onChange, path);
}

export function ContentEditor({ data, onChange }) {
  return (
    <div data-testid="content-editor">
      {renderNode(data, onChange, "ce", Array.isArray(data) ? "case" : "")}
    </div>
  );
}

export default ContentEditor;
